import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './AdminLayout.scss';

// Minimal SVG icons — no emoji, no icon library needed
const IconDashboard = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
    <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
    <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
    <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
  </svg>
);

const IconSlots = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="3" width="13" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 1.5V4M11 1.5V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M1.5 6.5H14.5" stroke="currentColor" strokeWidth="1.25"/>
    <rect x="4" y="9" width="2" height="2" rx="0.5" fill="currentColor"/>
    <rect x="7" y="9" width="2" height="2" rx="0.5" fill="currentColor"/>
    <rect x="10" y="9" width="2" height="2" rx="0.5" fill="currentColor"/>
  </svg>
);

const IconAppointments = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M4.5 6.5H11.5M4.5 9H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="12" cy="12" r="3" fill="currentColor"/>
    <path d="M11 12l.8.8L13.2 11" stroke="white" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconSchedule = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 1.5V4M11 1.5V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M1.5 6.5H14.5" stroke="currentColor" strokeWidth="1.25"/>
    <path d="M5 9.5H8M5 12H10" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/>
    <circle cx="12" cy="11" r="2.5" fill="currentColor"/>
    <path d="M11.2 11l.6.6L13 10.2" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconLogout = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10.5 11L14 8l-3.5-3M6.5 8H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconMenu = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M2.5 4.5h13M2.5 9h13M2.5 13.5h13" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
  </svg>
);

const NAV_ITEMS = [
  { to: '/admin/dashboard',    Icon: IconDashboard,    label: 'Dashboard' },
  { to: '/admin/slots',        Icon: IconSlots,        label: 'Slots' },
  { to: '/admin/appointments', Icon: IconAppointments, label: 'Appointments' },
  { to: '/admin/schedule',     Icon: IconSchedule,     label: 'Schedule' },
];

export default function AdminLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  function handleLogout() {
    logout();
    navigate('/admin/login', { replace: true });
  }

  return (
    <div className="al-layout">
      <aside className={`al-sidebar ${sidebarOpen ? 'al-sidebar--open' : ''}`}>
        <div className="al-sidebar-brand">
          <img src="/favicon.png" className="al-brand-logo" alt="Dentalis" />
          <span className="al-sidebar-name">Dentalis</span>
        </div>

        <nav className="al-sidebar-nav">
          {NAV_ITEMS.map(({ to, Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `al-nav-item ${isActive ? 'al-nav-item--active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <span className="al-nav-icon"><Icon /></span>
              <span className="al-nav-label">{label}</span>
            </NavLink>
          ))}
        </nav>

        <button className="al-logout-btn" onClick={handleLogout}>
          <span className="al-nav-icon"><IconLogout /></span>
          <span className="al-nav-label">Logout</span>
        </button>
      </aside>

      {sidebarOpen && (
        <div className="al-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="al-main">
        <header className="al-topbar">
          <button
            className="al-hamburger"
            onClick={() => setSidebarOpen(s => !s)}
            aria-label="Toggle menu"
          >
            <IconMenu />
          </button>
          <span className="al-topbar-title">Admin</span>
        </header>

        <main className="al-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
